import { ESdChangeReason, ICommonSettings } from "@kuika/kuika-cl-model";
import { Form } from "antd/es";
import { Fragment, ReactNode } from "react";
import { IWithSDProps, withSD } from "../../../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../../../shared/pure-sd";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import KuikaSelectbox, { KuikaSelectboxOption } from "../../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaTabContent from "../../../../ui-kit/kuika-tab-content/kuika-tab-content";
import KuikaTabs from "../../../../ui-kit/kuika-tabs/kuika-tabs";
import { ConfigModalNote } from "../config-modal-parts";
import { ConfigField } from "../config-pane-parts";
import { ConfigParameterList, IConfigParameter } from "./config-parameter-list";
import "./config-auth-provider.scss";

/** The fields inside this dialog, for the settings search. See `IConfigFieldProps.searchTerms`. */
export const APP_AUTH_GENERIC_SEARCH_TERMS: string[] = [
  "URL",
  "URL Content Type",
  "Expiration In Minutes",
  "Parameters",
  "Token URL",
  "Token URL Content Type",
  "Access Token",
  "User Name",
  "First Name",
  "Last Name",
  "Refresh Token"
];

const CONTENT_TYPES: KuikaSelectboxOption[] = [
  { value: "application/json", label: "application/json" },
  { value: "application/x-www-form-urlencoded", label: "application/x-www-form-urlencoded" }
];

/** The panes, in the order the strip shows them. The labels are the strip's own locators. */
const TABS: readonly string[] = ["TOKEN", "REFRESH TOKEN", "RESPONSE"];
const TAB_KEYS: Record<string, string> = {
  TOKEN: "token",
  "REFRESH TOKEN": "refreshToken",
  RESPONSE: "response"
};
const TAB_LABELS: Record<string, string> = {
  token: "TOKEN",
  refreshToken: "REFRESH TOKEN",
  response: "RESPONSE"
};
/** The panes' keys in the strip's order, so the incoming pane slides in from the side the tab moved to. */
const TAB_ORDER: string[] = TABS.map((label: string): string => TAB_KEYS[label]);

export interface IAppConfigAuthGenericState {}

export interface IAppConfigAuthGenericProps {
  editingId?: string;
  tokenParameters: IConfigParameter[];
  refreshTokenParameters: IConfigParameter[];
  activeKey?: string;
  getSectionDescription: (contentKey: string) => ReactNode;
  handleTabChange: (activeKey: string) => void;
  /**
   * Adds a ready-made parameter. The key is which of the menu's entries was chosen — it was
   * antd's `Menu` click event before, and the owner has always read nothing but `e.key`.
   */
  handleClickAddButton: (key: string, type: string) => void;
  onTokenParameterChange: (parameters: IConfigParameter[]) => void;
  onRefreshTokenParameterChange: (parameters: IConfigParameter[]) => void;
  commonSettings: ICommonSettings;
}

class AppConfigAuthGeneric extends PureSdComponent<
  IAppConfigAuthGenericProps & IWithSDProps,
  IAppConfigAuthGenericState
> {
  constructor(props: IAppConfigAuthGenericProps & IWithSDProps) {
    super(props);
    this.state = {};
  }

  existingNameValidator = async (_rule, value) => {
    const settings = this.props.commonSettings?.genericAuthSettings;

    const isSettingAlreadyExist =
      settings?.some(
        (x) => x.name === value && x.id !== this.props.editingId && !x.isGlobalCommonConfigOnProjectConfig
      ) || value === "Kuika Auth";

    if (isSettingAlreadyExist) throw new Error("Generic Auth Settings Name is already exist.");
  };

  private activeKey = (): string => this.props.activeKey ?? "token";

  render() {
    return (
      <div className="kuika-auth-provider">
        <ConfigField
          label="Name"
          required
          tooltipContentKey="authenticationprovidersettings_genericauth_authenticationprovidersettings_name"
        >
          <Form.Item
            name="genericAuthName"
            rules={[
              { required: true, message: "Please enter a Generic Auth settings name" },
              { pattern: /^[A-Za-z][A-Za-z0-9 ]+$/, message: "Invalid Generic Auth settings name" },
              { validator: this.existingNameValidator }
            ]}
          >
            <KuikaInput size="md" />
          </Form.Item>
        </ConfigField>

        <KuikaTabs
          className="kuika-auth-provider__tabs"
          options={TABS as string[]}
          value={TAB_LABELS[this.activeKey()]}
          onChange={(label: string): void => this.props.handleTabChange(TAB_KEYS[label])}
        />

        {/*
          Every pane stays mounted whichever one is showing (`keepMounted`), hidden rather than
          swapped out. antd validates and submits only the fields that are mounted, so a
          required Access Token in an unvisited pane would pass submit silently — which is why
          all three carried `forceRender` when this was an antd `Tabs`.
        */}
        <KuikaTabContent activeKey={this.activeKey()} order={TAB_ORDER} keepMounted>
          <Fragment key="token">
            <ConfigModalNote>
              {this.props.getSectionDescription(
                "authenticationprovidersettings_genericauth_authenticationprovidersettings_token_description"
              )}
            </ConfigModalNote>
            <ConfigField
              label="URL"
              tooltipContentKey="authenticationprovidersettings_genericauth_authenticationprovidersettings_token_url"
            >
              <Form.Item name="tokenUrl">
                <KuikaInput size="md" />
              </Form.Item>
            </ConfigField>
            <ConfigField
              label="URL Content Type"
              tooltipContentKey="authenticationprovidersettings_genericauth_authenticationprovidersettings_token_urlcontenttype"
            >
              <Form.Item name="tokenContentType">
                <KuikaSelectbox size="md" placeholder="Content Type" options={CONTENT_TYPES} />
              </Form.Item>
            </ConfigField>
            <ConfigField
              label="Expiration In Minutes"
              tooltipContentKey="authenticationprovidersettings_genericauth_authenticationprovidersettings_token_expirationinminutes"
            >
              <Form.Item name="tokenExpirationInMinutes">
                <KuikaInput size="md" type="number" min={0} />
              </Form.Item>
            </ConfigField>
            <ConfigField
              label="Parameters"
              tooltipContentKey="authenticationprovidersettings_genericauth_authenticationprovidersettings_token_parameters"
            >
              <ConfigParameterList
                parameters={this.props.tokenParameters}
                onChange={this.props.onTokenParameterChange}
                lockedValues={{ username: "{username}", password: "{password}" }}
                onAdd={(key: string): void => this.props.handleClickAddButton(key, "token")}
                addOptions={[
                  { key: "1", label: "Custom" },
                  {
                    key: "2",
                    label: "Username",
                    disabled: this.props.tokenParameters.some((x: IConfigParameter): boolean => x.name === "username")
                  },
                  {
                    key: "3",
                    label: "Password",
                    disabled: this.props.tokenParameters.some((x: IConfigParameter): boolean => x.name === "password")
                  }
                ]}
              />
            </ConfigField>
          </Fragment>

          <Fragment key="refreshToken">
            <ConfigModalNote>
              {this.props.getSectionDescription(
                "authenticationprovidersettings_genericauth_authenticationprovidersettings_refreshtoken_description"
              )}
            </ConfigModalNote>
            <ConfigField
              label="Token URL"
              tooltipContentKey="authenticationprovidersettings_genericauth_authenticationprovidersettings_refreshtoken_tokenurl"
            >
              <Form.Item name="refreshTokenUrl">
                <KuikaInput size="md" />
              </Form.Item>
            </ConfigField>
            <ConfigField
              label="Token URL Content Type"
              tooltipContentKey="authenticationprovidersettings_genericauth_authenticationprovidersettings_refreshtoken_tokenurlcontenttype"
            >
              <Form.Item name="refreshTokenContentType">
                <KuikaSelectbox size="md" placeholder="Content Type" options={CONTENT_TYPES} />
              </Form.Item>
            </ConfigField>
            <ConfigField
              label="Parameters"
              tooltipContentKey="authenticationprovidersettings_genericauth_authenticationprovidersettings_refreshtoken_parameters"
            >
              <ConfigParameterList
                parameters={this.props.refreshTokenParameters}
                onChange={this.props.onRefreshTokenParameterChange}
                lockedValues={{ token: "{token}", refreshtoken: "{refreshtoken}" }}
                onAdd={(key: string): void => this.props.handleClickAddButton(key, "refreshtoken")}
                addOptions={[
                  { key: "1", label: "Custom" },
                  { key: "2", label: "Token" },
                  { key: "3", label: "Refresh Token" }
                ]}
              />
            </ConfigField>
          </Fragment>

          <Fragment key="response">
            <ConfigModalNote>
              {this.props.getSectionDescription(
                "authenticationprovidersettings_genericauth_authenticationprovidersettings_repsonse_description"
              )}
            </ConfigModalNote>
            <ConfigField
              label="Access Token"
              required
              tooltipContentKey="authenticationprovidersettings_genericauth_authenticationprovidersettings_repsonse_accesstoken"
            >
              <Form.Item
                name="accessTokenKeyName"
                rules={[{ required: true, message: "Please enter a Access Token key name." }]}
              >
                <KuikaInput size="md" />
              </Form.Item>
            </ConfigField>
            <ConfigField
              label="User Name"
              required
              tooltipContentKey="authenticationprovidersettings_genericauth_authenticationprovidersettings_repsonse_username"
            >
              <Form.Item
                name="userNameKeyName"
                rules={[{ required: true, message: "Please enter your user name key name!" }]}
              >
                <KuikaInput size="md" />
              </Form.Item>
            </ConfigField>
            <ConfigField
              label="First Name"
              tooltipContentKey="authenticationprovidersettings_genericauth_authenticationprovidersettings_repsonse_firstname"
            >
              <Form.Item name="firstNameKeyName">
                <KuikaInput size="md" />
              </Form.Item>
            </ConfigField>
            <ConfigField
              label="Last Name"
              tooltipContentKey="authenticationprovidersettings_genericauth_authenticationprovidersettings_repsonse_lastname"
            >
              <Form.Item name="lastNameKeyName">
                <KuikaInput size="md" />
              </Form.Item>
            </ConfigField>
            <ConfigField
              label="Refresh Token"
              tooltipContentKey="authenticationprovidersettings_genericauth_authenticationprovidersettings_repsonse_refreshtoken"
            >
              <Form.Item name="refreshTokenKeyName">
                <KuikaInput size="md" />
              </Form.Item>
            </ConfigField>
          </Fragment>
        </KuikaTabContent>
      </div>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    return false;
  };
}

const appConfigAuthGeneric = withSD<IAppConfigAuthGenericProps>(AppConfigAuthGeneric);
export { appConfigAuthGeneric as AppConfigAuthGeneric };
